import { 
  ArrowLeftOutlined, 
  CheckOutlined, 
  DownloadOutlined, 
  EditOutlined, 
  FilePdfOutlined,
  LeftOutlined, 
  LoadingOutlined, 
  RightOutlined,
  ShoppingCartOutlined 
} from '@ant-design/icons';
import { PrimaryButton } from '@components/atoms';
import { useCartDrawer } from '@contexts';
import { useAuth, useCart, useProducts } from '@hooks';
import { Empty, Input, InputNumber, message, Spin, Tag, Typography } from 'antd';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const { Title, Paragraph } = Typography;

export const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getProduct, updateProduct } = useProducts();
  const { isAuthenticated, profile } = useAuth();
  const isAdmin = profile.data?.profile === 'admin';
  const { addToCart } = useCart(isAuthenticated);
  const { openDrawer } = useCartDrawer();
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null);
  
  // SKU editing state (admin only)
  const [isEditingSku, setIsEditingSku] = useState(false);
  const [editingSkuValue, setEditingSkuValue] = useState('');
  const [isSavingSku, setIsSavingSku] = useState(false);

  const { data: product, isLoading, error, refetch } = getProduct(id!);

  // Set default variant when product loads
  useEffect(() => {
    if (product && product.variants && product.variants.length > 0) {
      // Se não há tamanho selecionado, usar a primeira variante como padrão
      if (!selectedSize) {
        setSelectedVariant(product.variants[0]);
        setSelectedSize(product.variants[0].size);
        setSelectedImageIndex(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  // Handle SKU edit start
  const handleStartEditSku = () => {
    setEditingSkuValue(selectedVariant?.sku || '');
    setIsEditingSku(true);
  };

  // Handle SKU edit save
  const handleSaveSku = async () => {
    if (!product || isSavingSku) return;
    
    const newSku = editingSkuValue.trim();
    
    // If SKU hasn't changed, just close the editor
    if (newSku === (selectedVariant?.sku || '')) {
      setIsEditingSku(false);
      return;
    }
    
    // SKU editing is only available for products with variants
    if (!selectedVariant) {
      message.error('SKU can only be edited for products with variants');
      setIsEditingSku(false);
      return;
    }
    
    setIsSavingSku(true);
    try {
      // Update the variant's SKU by updating the entire variants array
      const updatedVariants = product.variants?.map(variant => 
        variant.id === selectedVariant.id 
          ? { ...variant, sku: newSku }
          : variant
      ) || [];
      
      await updateProduct.mutateAsync({
        id: product.id,
        data: { 
          variants: updatedVariants.map(v => ({ 
            sku: v.sku, 
            size: v.size, 
            price: v.price, 
            stockQuantity: v.stockQuantity 
          }))
        }
      });
      await refetch();
      setIsEditingSku(false);
      message.success('SKU updated successfully!');
    } catch (error) {
      console.error('Failed to update SKU:', error);
      message.error('Failed to update SKU');
    } finally {
      setIsSavingSku(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getImageUrl = (imageUrl: string) => {
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'ruppert-production';
    const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
    return `https://${bucketName}.s3.${region}.amazonaws.com/${imageUrl}`;
  };

  const getProductImage = (product: any, index = 0) => {
    // Use images array if available
    if (product.images && product.images.length > 0) {
      const image = product.images[index] || product.images[0];
      return getImageUrl(image);
    }

    // Fallback to legacy imageUrl
    if (product.imageUrl) {
      return getImageUrl(product.imageUrl);
    }

    // Ultimate fallback
    return `https://placehold.co/600x600/181717/E3AF25?text=${encodeURIComponent(product.name)}`;
  };

  const handleAddToCart = async () => {
    if (!product) return;

    // Check if product has variants and if one is selected
    if (product.variants && product.variants.length > 0) {
      if (!selectedVariant || !selectedSize) {
        message.warning('Please select a size before adding to cart');
        return;
      }
      
      // Check stock for selected variant
      const availableStock = selectedVariant.stockQuantity;
      
      // Check if there's enough stock
      if (availableStock < quantity) {
        message.warning(`Only ${availableStock} items available in stock for size ${selectedSize}`);
        return;
      }
    } else {
      // For products without variants, check general stockQuantity
      if (product.stockQuantity !== undefined && product.stockQuantity < quantity) {
        message.warning(`Only ${product.stockQuantity} items available in stock`);
        return;
      }
    }

    setIsAddingToCart(true);
    try {
      await addToCart(product.id, quantity, product, selectedVariant?.id || null, selectedSize || null);
      message.success(`Added ${quantity} ${product.name}${selectedSize ? ` (Size: ${selectedSize})` : ''} to cart!`);
      openDrawer();
    } catch (error) {
      console.error('Failed to add to cart:', error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleQuantityChange = (value: number | null) => {
    if (value && value > 0) {
      const maxStock = selectedVariant?.stockQuantity ?? product?.stockQuantity ?? 999;
      if (value > maxStock) {
        setQuantity(maxStock);
        message.warning(`Maximum ${maxStock} items available`);
      } else {
        setQuantity(value);
      }
    }
  };

  const handleContactClick = () => {
    navigate('/contact');
    window.scrollTo(0, 0);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Empty
            description={
              <span className="text-gray-400 text-lg font-barlow">
                Product not found
              </span>
            }
          />
          <PrimaryButton
            onClick={() => navigate('/merch')}
            className="mt-6"
            size="large"
          >
            <ArrowLeftOutlined className="mr-2" /> BACK TO PRODUCTS
          </PrimaryButton>
        </div>
      </div>
    );
  }

  const images = product.images && product.images.length > 0 ? product.images : (product.imageUrl ? [product.imageUrl] : []);
  const currentStock = selectedVariant?.stockQuantity ?? product.stockQuantity ?? 0;
  const isOutOfStock = currentStock <= 0;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Product Details Section */}
      <section className="py-8 md:py-12 lg:py-16 bg-first-dark-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          {/* Product Name - Mobile: above image, Desktop: aligned with description */}
          <div className="mb-6 lg:mb-0">
            <div className="lg:hidden mb-6">
              <Title level={1} className="font-android !font-normal text-yellow-principal uppercase !mb-0 mt-8 !text-2xl sm:!text-3xl md:!text-4xl text-center" style={{ lineHeight: '1.2' }}>
                {product?.name}
              </Title>
            </div>
            <div className="hidden lg:grid lg:grid-cols-2 gap-8 xl:gap-16 mb-4">
              <div></div>
              <div>
                <Title level={1} className="font-android !font-normal text-yellow-principal uppercase !mb-0" style={{ fontSize: '2.5rem', lineHeight: '1' }}>
                  {product?.name}
                </Title>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 xl:gap-16">
            {/* Product Image Carousel */}
            <div className="w-full">
              <div className="relative flex items-stretch gap-2 sm:gap-3 md:gap-4">
                {/* Left Arrow with Gradient */}
                {images.length > 1 && (
                  <div className="relative flex items-center justify-center w-10 sm:w-14 md:w-16 lg:w-20">
                    <div 
                      className="absolute inset-0 pointer-events-none z-0"
                      style={{
                        background: 'linear-gradient(to left, rgba(200, 200, 200, 0.1) 0%, rgba(200, 200, 200, 0.05) 50%, rgba(200, 200, 200, 0) 100%)'
                      }}
                    />
                    <button
                      onClick={() => setSelectedImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
                      className="cursor-pointer hover:text-yellow-principal transition-colors relative z-20 w-full h-full flex items-center justify-center"
                      style={{ color: '#8F8F8F' }}
                      aria-label="Previous image"
                    >
                      <LeftOutlined className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl" />
                    </button>
                  </div>
                )}
                
                {/* Main Image */}
                <div className="relative overflow-hidden bg-black flex-1 max-w-full">
                  <div className="w-full aspect-square mx-auto max-h-[45vh] sm:max-h-[50vh] md:max-h-[55vh] lg:max-h-none">
                    <img
                      src={getProductImage(product, selectedImageIndex)}
                      alt={product?.name || 'Product'}
                      className="w-full h-full object-contain relative z-10"
                      onError={(e) => {
                        e.currentTarget.src = `https://placehold.co/600x600/181717/E3AF25?text=${encodeURIComponent(product?.name || 'Product')}`;
                      }}
                    />
                    
                    {/* Out of Stock overlay */}
                    {isOutOfStock && (
                      <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20">
                        <span className="text-white font-android font-bold text-lg sm:text-xl md:text-2xl">OUT OF STOCK</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Right Arrow with Gradient */}
                {images.length > 1 && (
                  <div className="relative flex items-center justify-center w-10 sm:w-14 md:w-16 lg:w-20">
                    <div 
                      className="absolute inset-0 pointer-events-none z-0"
                      style={{
                        background: 'linear-gradient(to right, rgba(200, 200, 200, 0.1) 0%, rgba(200, 200, 200, 0.05) 50%, rgba(200, 200, 200, 0) 100%)'
                      }}
                    />
                    <button
                      onClick={() => setSelectedImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
                      className="cursor-pointer hover:text-yellow-principal transition-colors relative z-20 w-full h-full flex items-center justify-center"
                      style={{ color: '#8F8F8F' }}
                      aria-label="Next image"
                    >
                      <RightOutlined className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl" />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Carousel Indicators */}
              {images.length > 1 && (
                <div className="flex justify-center gap-2 mt-4 md:mt-6">
                  {images.map((_: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`h-2 md:h-3 rounded-full transition-all ${
                        selectedImageIndex === index ? 'w-5 md:w-6 bg-yellow-principal' : 'w-2 md:w-3 bg-zinc-600'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-4 md:space-y-6">
              {/* SKU */}
              {(selectedVariant?.sku || isAdmin) && (
                <div className="mb-2">
                  {isAdmin && isEditingSku ? (
                    <div className="flex items-center gap-1">
                      <span className="font-android text-yellow-principal text-xs sm:text-sm tracking-wider">SKU:</span>
                      <span className="font-android text-white text-xs sm:text-sm tracking-wider">#</span>
                      <Input
                        value={editingSkuValue}
                        onChange={(e) => setEditingSkuValue(e.target.value)}
                        onPressEnter={handleSaveSku}
                        onBlur={handleSaveSku}
                        autoFocus
                        size="small"
                        disabled={isSavingSku}
                        className="!w-20 sm:!w-24 !bg-transparent !border-b !border-t-0 !border-l-0 !border-r-0 !border-yellow-principal !rounded-none !text-white font-android text-xs sm:text-sm !px-0 !shadow-none focus:!shadow-none"
                        style={{ background: 'transparent' }}
                      />
                      {isSavingSku ? (
                        <LoadingOutlined className="text-yellow-principal text-xs" />
                      ) : (
                        <CheckOutlined 
                          className="text-yellow-principal text-xs cursor-pointer hover:text-white transition-colors" 
                          onClick={handleSaveSku}
                        />
                      )}
                    </div>
                  ) : (
                    <span 
                      className={`font-android text-xs sm:text-sm tracking-wider whitespace-nowrap flex items-center gap-1 ${isAdmin ? 'cursor-pointer group' : ''}`}
                      onClick={isAdmin ? handleStartEditSku : undefined}
                    >
                      <span className="text-yellow-principal">SKU:</span>
                      <span className="text-white">#{selectedVariant?.sku || (isAdmin ? 'Add SKU' : '')}</span>
                      {isAdmin && (
                        <EditOutlined className="ml-1 text-xs text-yellow-principal opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </span>
                  )}
                </div>
              )}

              {/* Category Tag */}
              {product.category && (
                <Tag className="bg-zinc-800 text-white border-none font-barlow px-2 sm:px-3 py-1 !mb-4 md:!mb-6 !rounded-none text-xs sm:text-sm">
                  {product.category}
                </Tag>
              )}

              {/* Description Box */}
              <div>
                <div className="p-0">
                  <Paragraph className="font-barlow text-gray-300 text-sm sm:text-base leading-relaxed !mb-0 text-left">
                    {product?.description || 'No description available for this product.'}
                  </Paragraph>
                </div>
              </div>

              {/* Price */}
              {selectedVariant?.price && (
                <div className="font-android text-yellow-principal !font-bold text-2xl sm:text-3xl md:text-4xl tracking-wider">
                  {formatPrice(selectedVariant.price)}
                </div>
              )}

              {/* Stock Status */}
              {(selectedVariant?.stockQuantity !== undefined || product.stockQuantity !== undefined) && (
                <div>
                  {(() => {
                    const currentStock = selectedVariant?.stockQuantity ?? product.stockQuantity ?? 0;
                    const isCurrentlyOutOfStock = currentStock <= 0;
                    
                    if (isCurrentlyOutOfStock) {
                      return (
                        <Tag color="red" className="text-xs sm:text-sm md:text-base px-3 sm:px-4 py-1 font-barlow">
                          Out of Stock
                        </Tag>
                      );
                    } else if (currentStock <= 5) {
                      return (
                        <Tag color="orange" className="text-xs sm:text-sm md:text-base px-3 sm:px-4 py-1 font-barlow">
                          Only {currentStock} left in stock
                        </Tag>
                      );
                    } else {
                      return (
                        <Tag color="green" className="text-xs sm:text-sm md:text-base px-3 sm:px-4 py-1 font-barlow">
                          In Stock
                        </Tag>
                      );
                    }
                  })()}
                </div>
              )}

              {/* Sizes */}
              {product.variants && product.variants.length > 0 && (
                <div>
                  <Title level={4} className="!text-white mb-2 md:mb-3 font-android !font-normal !text-base sm:!text-lg md:!text-xl">
                    AVAILABLE SIZES
                  </Title>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map((variant: any) => {
                      const isSelected = selectedSize === variant.size;
                      const isOutOfStock = variant.stockQuantity <= 0;
                      return (
                        <button
                          key={variant.id || variant.size}
                          onClick={() => {
                            setSelectedSize(variant.size);
                            setSelectedVariant(variant);
                            setSelectedImageIndex(0); // Reset image index when variant changes
                          }}
                          disabled={isOutOfStock}
                          className={`px-3 sm:px-4 py-1.5 sm:py-2 border-2 transition-all font-barlow text-xs sm:text-sm md:text-base ${
                            isOutOfStock
                              ? 'border-zinc-700 text-zinc-600 cursor-not-allowed opacity-50'
                              : isSelected
                              ? 'border-yellow-principal bg-yellow-principal text-black'
                              : 'border-zinc-600 text-gray-300 hover:border-yellow-principal hover:text-yellow-principal'
                          }`}
                        >
                          {variant.size}
                          {isOutOfStock && <span className="hidden sm:inline"> (Out of Stock)</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              {!isOutOfStock && (
                <div className="bg-zinc-800 py-2 sm:py-3 px-3 sm:px-4 flex items-center gap-2 sm:gap-4">
                  <label className="font-android text-white text-sm sm:text-base md:text-lg whitespace-nowrap">QUANTITY:</label>
                  <InputNumber
                    min={1}
                    max={selectedVariant?.stockQuantity || product.stockQuantity || 999}
                    value={quantity}
                    onChange={handleQuantityChange}
                    className="quantity-input font-android text-lg sm:text-xl md:text-2xl font-normal flex-1"
                    style={{ 
                      width: '100%',
                      backgroundColor: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      color: 'white'
                    }}
                    controls={false}
                  />
                  <span className="font-android text-white !font-normal tracking-wider uppercase text-sm sm:text-base md:!text-xl">PCS</span>
                </div>
              )}

              {/* Add to Cart Button */}
              <div>
                <PrimaryButton
                  size="large"
                  block
                  icon={<ShoppingCartOutlined />}
                  onClick={handleAddToCart}
                  loading={isAddingToCart}
                  disabled={isOutOfStock || !!(product.variants && product.variants.length > 0 && !selectedVariant)}
                  className="h-12 sm:h-14 text-base sm:text-lg uppercase"
                >
                  {isOutOfStock 
                    ? 'OUT OF STOCK' 
                    : product.variants && product.variants.length > 0 && !selectedVariant
                      ? 'SELECT SIZE'
                      : 'ADD TO CART'}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      {product.videoUrl && (
        <section className="py-8 md:py-12 lg:py-16 bg-second-black-carbon">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <Title level={2} className="font-android !font-normal text-yellow-principal uppercase mb-6 md:mb-8 lg:mb-12 text-center tracking-wider md:tracking-widest !text-xl sm:!text-2xl md:!text-3xl">
              PRODUCT VIDEO
            </Title>
            <div className="max-w-4xl mx-auto">
              <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-sm">
                <iframe
                  src={product.videoUrl}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Product Video"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Technical Documents Section */}
      {product.documents && product.documents.length > 0 && (
        <section className="py-8 md:py-12 lg:py-16 bg-first-dark-gray">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <Title level={2} className="font-android !font-normal text-yellow-principal uppercase !mb-6 md:!mb-8 lg:!mb-12 text-center tracking-wider !text-xl sm:!text-2xl md:!text-3xl">
              TECHNICAL DOCUMENTS
            </Title>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
              {product.documents.map((doc: any) => (
                <div
                  key={doc.id}
                  className="relative cursor-pointer transition-all hover:scale-105"
                  onClick={() => window.open(getImageUrl(doc.fileUrl), '_blank')}
                >
                  <div 
                    className="relative bg-first-dark-gray border-2 sm:border-4 border-zinc-600 flex flex-col items-center p-4 sm:p-6 md:p-8 text-center h-full"
                    style={{
                      clipPath: 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)'
                    }}
                  >
                    {/* Corner decorations */}
                    <div
                      className="pointer-events-none absolute top-0 left-0"
                      style={{ 
                        width: 20, 
                        height: 20, 
                        clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                        backgroundColor: '#52525B'
                      }}
                    />
                    <div
                      className="pointer-events-none absolute bottom-0 right-0"
                      style={{ 
                        width: 20, 
                        height: 20, 
                        clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                        backgroundColor: '#52525B'
                      }}
                    />
                    <FilePdfOutlined className="text-3xl sm:text-4xl md:text-5xl text-yellow-principal mb-3 sm:mb-4 relative z-10" />
                    <Title level={5} className="font-barlow !font-normal text-white mb-2 relative z-10 !text-sm sm:!text-base">
                      {doc.name}
                    </Title>
                    {doc.description && (
                      <Paragraph className="font-barlow !text-gray-300 relative z-10 !text-xs sm:!text-sm">
                        {doc.description}
                      </Paragraph>
                    )}
                    <PrimaryButton
                      icon={<DownloadOutlined />}
                      className="relative z-10 uppercase mt-3 sm:mt-4 !text-xs sm:!text-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(getImageUrl(doc.fileUrl), '_blank');
                      }}
                    >
                      DOWNLOAD
                    </PrimaryButton>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Products Section */}
      {product.relatedProducts && product.relatedProducts.length > 0 && (
        <section className="py-8 md:py-12 lg:py-16 bg-first-dark-gray">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <Title level={2} className="font-android !font-normal text-yellow-principal uppercase mb-6 md:mb-8 lg:mb-12 text-center tracking-wider md:tracking-widest !text-xl sm:!text-2xl md:!text-3xl">
              RELATED PRODUCTS
            </Title>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
              {product.relatedProducts.slice(0, 4).map((relatedProduct: any) => {
                const relatedImages = relatedProduct.images && relatedProduct.images.length > 0 
                  ? relatedProduct.images 
                  : relatedProduct.imageUrl ? [relatedProduct.imageUrl] : [];
                const relatedImageUrl = relatedImages.length > 0 
                  ? getImageUrl(relatedImages[0])
                  : `https://placehold.co/400x400/181717/E3AF25?text=${encodeURIComponent(relatedProduct.name)}`;
                
                return (
                  <div
                    key={relatedProduct.id}
                    className="cursor-pointer transition-all hover:scale-105"
                    onClick={() => {
                      navigate(`/merch/${relatedProduct.id}`);
                      window.scrollTo(0, 0);
                    }}
                  >
                    {/* Product Image with Gradient */}
                    <div className="relative aspect-square overflow-hidden bg-transparent mb-4">
                      <img 
                        alt={relatedProduct.name} 
                        src={relatedImageUrl}
                        className="w-full h-full object-contain relative z-10"
                        onError={(e) => {
                          e.currentTarget.src = `https://placehold.co/400x400/181717/E3AF25?text=${encodeURIComponent(relatedProduct.name)}`;
                        }}
                      />
                      {/* Gradient overlay */}
                      <div 
                        className="absolute inset-0 pointer-events-none z-0"
                        style={{
                          background: 'linear-gradient(to top, rgba(227, 175, 37, 0.5) 0%, rgba(227, 175, 37, 0.2) 40%, rgba(227, 175, 37, 0) 85%)'
                        }}
                      />
                    </div>
                    
                    {/* Product Name with Border */}
                    <div className="relative">
                      <div 
                        className="relative border-2 sm:border-4 pt-4 sm:pt-6 px-3 sm:px-6 pb-3 sm:pb-4"
                        style={{ 
                          clipPath: 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)',
                          borderColor: '#D9D9D9'
                        }}
                      >
                        {/* Corner decorations */}
                        <div
                          className="pointer-events-none absolute top-0 left-0"
                          style={{ 
                            width: 20, 
                            height: 20, 
                            clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                            backgroundColor: '#D9D9D9'
                          }}
                        />
                        <div
                          className="pointer-events-none absolute bottom-0 right-0"
                          style={{ 
                            width: 20, 
                            height: 20, 
                            clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                            backgroundColor: '#D9D9D9'
                          }}
                        />
                        <h3 className="font-android text-white text-center text-xs sm:text-sm uppercase tracking-wider !mb-1 sm:!mb-2 relative z-10">
                          {relatedProduct.name}
                        </h3>
                        {relatedProduct.category && (
                          <Paragraph className="font-barlow text-gray-400 mb-0 text-center text-xs relative z-10">
                            {relatedProduct.category}
                          </Paragraph>
                        )}
                        {relatedProduct.variants && relatedProduct.variants.length > 0 && (
                          <Paragraph className="font-android text-yellow-principal mb-0 text-center text-base sm:text-lg relative z-10 mt-1 sm:mt-2">
                            {(() => {
                              const prices = relatedProduct.variants.map((v: any) => v.price);
                              const minPrice = Math.min(...prices);
                              const maxPrice = Math.max(...prices);
                              if (minPrice === maxPrice) {
                                return formatPrice(minPrice);
                              }
                              return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
                            })()}
                          </Paragraph>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Need Help Section */}
      <section className="bg-second-black-carbon py-10 md:py-16 lg:py-20 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Decorative squares */}
          <div className="flex justify-center items-center gap-2 sm:gap-3 md:gap-4 lg:gap-6 mb-6 md:mb-8 lg:mb-12">
            {[1, 2, 3, 4, 5].map((index) => (
              <div
                key={index}
                className={`relative w-10 h-10 sm:w-12 sm:h-12 md:w-20 md:h-20 lg:w-28 lg:h-28 flex items-center justify-center ${
                  index === 3 
                    ? 'border-2 border-yellow-principal' 
                    : 'border border-gray-600'
                }`}
                style={{ clipPath: 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)' }}
              >
                {/* Corner decoration - top left triangle */}
                <div
                  className={`pointer-events-none absolute top-0 left-0 ${
                    index === 3 ? 'bg-yellow-principal' : 'bg-gray-600'
                  }`}
                  style={{ width: 12, height: 12, clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                />
                {/* Corner decoration - bottom right triangle */}
                <div
                  className={`pointer-events-none absolute bottom-0 right-0 ${
                    index === 3 ? 'bg-yellow-principal' : 'bg-gray-600'
                  }`}
                  style={{ width: 12, height: 12, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                />
                {/* Content of middle square */}
                {index === 3 && (
                  <span className="text-yellow-principal text-xl sm:text-2xl md:text-3xl lg:text-5xl font-android relative z-10">?</span>
                )}
              </div>
            ))}
          </div>

          <Title level={1} className="font-android !font-normal text-white uppercase !mb-0 text-lg sm:text-xl md:text-3xl lg:text-4xl xl:text-5xl px-4">
            NEED HELP FINDING THE RIGHT PRODUCT?
          </Title>
          <Paragraph className="font-barlow text-white max-w-3xl tracking-wide mx-auto mt-4 md:mt-6 !mb-6 md:!mb-8 lg:!mb-12 text-sm sm:text-base md:text-lg lg:text-xl px-4">
            Our team is available to assist you in selecting the perfect equipment for your needs.
          </Paragraph>
          <PrimaryButton 
            onClick={handleContactClick} 
            size="large"
            className="!h-12 sm:!h-14 !px-6 sm:!px-10 text-base sm:text-lg md:text-xl tracking-wider"
          >
            CONTACT US
          </PrimaryButton>
        </div>
      </section>

      {/* Our Family of Brands Section */}
      <section className="bg-first-dark-gray py-8 md:py-10 lg:py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Title level={2} className="font-android !font-normal text-white text-center uppercase mb-6 md:mb-8 lg:mb-10 !text-lg sm:!text-xl md:!text-2xl lg:!text-3xl">
            OUR FAMILY OF BRANDS
          </Title>
          
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 md:gap-8 lg:gap-12 xl:gap-16 pb-4 md:pb-6">
            <a 
              href="https://ruppertinc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[80px] h-[60px] sm:w-[100px] sm:h-[70px] md:w-[140px] md:h-[75px] lg:w-[180px] lg:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/ruppert-logo.svg" 
                alt="Ruppert Logo" 
                className="max-w-full max-h-full" 
              />
            </a>
            <a 
              href="https://estabrookinc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[80px] h-[60px] sm:w-[100px] sm:h-[70px] md:w-[140px] md:h-[75px] lg:w-[180px] lg:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/estabrook-logo.svg" 
                alt="Estabrook Logo" 
                className="max-w-full max-h-full" 
              />
            </a>
            <a 
              href="https://modlink.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[80px] h-[60px] sm:w-[100px] sm:h-[70px] md:w-[140px] md:h-[75px] lg:w-[180px] lg:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/modlink-logo.svg" 
                alt="Modlink Logo" 
                className="max-w-full max-h-full"
              />
            </a>
            <div className="w-[80px] h-[60px] sm:w-[100px] sm:h-[70px] md:w-[140px] md:h-[75px] lg:w-[180px] lg:h-[80px] flex items-center justify-center">
              <img 
                src="/assets/logos/snow-sync-logo.svg" 
                alt="Snow Sync Logo" 
                className="max-w-full max-h-full" 
              />
            </div>
            <div className="w-[80px] h-[60px] sm:w-[100px] sm:h-[70px] md:w-[120px] md:h-[75px] lg:w-[150px] lg:h-[80px] flex items-center justify-center">
              <img 
                src="/assets/logos/asr-logo.svg" 
                alt="ASR Logo" 
                className="max-w-full max-h-full"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
